import classNames from 'classnames';
import { Button, Spin } from 'lego-on-react';
import * as React from 'react';
import { InjectedRendererProps } from 'react-virtualized-tree';

import { Bem } from '../../../service/Bem/Bem';
import { divideNumberPositions } from '../../../service/FormatHelpers/FormatHelpers';
import { getPhraseNodeState } from '../../../service/PhrasesSelectorWizard/PhrasesSelectorWizard';
import { TestBem } from '../../../service/TestBem';
import {
    PhrasesSelectorPhrase,
    PhrasesSelectorStartPhraseFlattenedNode,
    PhrasesSelectorTreeUpdateType
} from '../../../typings/phrases';
import { CheckboxDecorator } from '../../CheckboxDecorator/CheckboxDecorator';
import { Shape2, Shape2Icon } from '../../Shape2/Shape2';
import { PhrasesSelectorStartWord } from '../PhrasesSelectorStartWord/PhrasesSelectorStartWord';

import './PhrasesSelectorPhrasesTreeNode.css';

const MAX_TREE_DEEPNESS = 6;

export interface PhrasesSelectorPhrasesTreeNodeProps {
    areMinusPhrasesEnabled: boolean;
    isGroupKeyword: boolean;
    node: PhrasesSelectorStartPhraseFlattenedNode;
    onChange: InjectedRendererProps<void>['onChange'];
    onKeywordAdd: (phrase: PhrasesSelectorPhrase) => void;
    onMinusPhraseAdd: (phrase: string) => void;
    onMinusWordAdd: (word: string) => void;
}

const BLOCK = 'phrases-selector-phrases-tree-node';

export class PhrasesSelectorPhrasesTreeNode extends React.Component<PhrasesSelectorPhrasesTreeNodeProps> {
    render() {
        const { node } = this.props;
        const { deepness, info } = node;
        const { loading, selected } = getPhraseNodeState(node);

        return (
            <div
                className={classNames(
                    Bem.cls(BLOCK, '', {
                        deepness: String(deepness)
                    }),
                    TestBem.cls(BLOCK, '', {
                        deepness: String(deepness)
                    })
                )}
            >
                <CheckboxDecorator
                    checked={selected}
                    size="s"
                    theme="normal"
                    tone="default"
                    view="default"
                    cls={Bem.cls(BLOCK, 'checkbox')}
                    onChecked={this.onSelect}
                />
                {this.renderTitle()}
                <Spin progress={loading} size="xxs" cls={Bem.cls(BLOCK, 'loader-spin')} />
                <div className={Bem.cls(BLOCK, 'right-column')}>
                    {this.renderControls()}
                    <span className={Bem.cls(BLOCK, 'shows-count')}>{divideNumberPositions(info.shows)}</span>
                </div>
            </div>
        );
    }

    private renderTitle = () => {
        const { areMinusPhrasesEnabled, isGroupKeyword, node, onMinusWordAdd } = this.props;
        const { deepness, name, info } = node;
        const { expanded, expandable } = getPhraseNodeState(node);
        const canExpand = expandable && deepness < MAX_TREE_DEEPNESS && info.shows !== 0;

        return (
            <div className={Bem.cls(BLOCK, 'title', { 'group-keyword': isGroupKeyword })}>
                <span className={classNames(Bem.cls(BLOCK, 'name'), TestBem.cls(BLOCK, 'name'))} title={name}>
                    {name.split(' ').map((word, key) => (
                        <PhrasesSelectorStartWord
                            key={key}
                            isShowMinusWordAdd={areMinusPhrasesEnabled}
                            word={word}
                            onMinusWordAdd={onMinusWordAdd}
                        />
                    ))}
                </span>
                {canExpand && (
                    <Button
                        theme="clear"
                        size="s"
                        cls={classNames(
                            Bem.cls(BLOCK, 'expand-button', {
                                expanded
                            }),
                            TestBem.cls(BLOCK, 'expand-button', {
                                expanded
                            })
                        )}
                        onClick={this.onExpandedToggle}
                    >
                        <Shape2 width={12} height={12} icon={Shape2Icon.verticalArrow} />
                    </Button>
                )}
            </div>
        );
    };

    private renderControls = () => {
        const { areMinusPhrasesEnabled, node } = this.props;
        const { loading } = getPhraseNodeState(node);

        return (
            <div className={Bem.cls(BLOCK, 'controls', { loading })}>
                {this.renderIconButton(Shape2Icon.plus, this.onKeywordAdd, TestBem.cls(BLOCK, 'add-phrase-btn'))}
                {areMinusPhrasesEnabled &&
                    this.renderIconButton(
                        Shape2Icon.minus,
                        this.onMinusPhraseAdd,
                        TestBem.cls(BLOCK, 'add-minus-phrase-btn')
                    )}
                {this.renderIconButton(Shape2Icon.trashbin, this.onRemove, TestBem.cls(BLOCK, 'remove-btn'))}
            </div>
        );
    };

    private renderIconButton = (icon: Shape2Icon, onClick: () => void, cls?: string) => {
        return (
            <Button theme="clear" size="s" cls={classNames(Bem.cls(BLOCK, 'icon-button'), cls)} onClick={onClick}>
                <Shape2 width={14} height={14} icon={icon} />
            </Button>
        );
    };

    private onExpandedToggle = () => {
        const { node, onChange } = this.props;
        const { expanded } = getPhraseNodeState(node);

        if (node.children && node.children.length !== 0) {
            onChange({
                node: {
                    ...node,
                    state: {
                        ...node.state,
                        expanded: !expanded
                    }
                },
                type: String(PhrasesSelectorTreeUpdateType.expand)
            });
        } else {
            onChange({
                node: {
                    ...node,
                    state: {
                        ...node.state,
                        loading: true
                    }
                },
                type: String(PhrasesSelectorTreeUpdateType.fetchChildren)
            });
        }
    };

    private onSelect = () => {
        const { node, onChange } = this.props;
        const { selected } = getPhraseNodeState(node);

        onChange({
            node: {
                ...node,
                state: {
                    ...node.state,
                    selected: !selected
                }
            },
            type: String(PhrasesSelectorTreeUpdateType.select)
        });
    };

    private onRemove = () => {
        const { node, onChange } = this.props;

        onChange({
            node,
            type: String(PhrasesSelectorTreeUpdateType.remove)
        });
    };

    private onKeywordAdd = () => {
        const {
            node: { name, info },
            onKeywordAdd
        } = this.props;

        onKeywordAdd({ name, shows: info.shows });
    };

    private onMinusPhraseAdd = () => {
        const { node, onMinusPhraseAdd } = this.props;

        onMinusPhraseAdd(node.name);
    };
}
